import $ from "jquery";
import flatpickr from "flatpickr";
import { CustomLocale, key } from "flatpickr/dist/types/locale";
import { Italian } from "flatpickr/dist/l10n/it.js";
import { French } from "flatpickr/dist/l10n/fr.js";
import { Spanish } from "flatpickr/dist/l10n/es.js";
import { english } from "flatpickr/dist/l10n/default.js";

export const InCoreDateTimePickerModes = {
    single: "single",
    multiple: "multiple",
    range: "range",
    time: "time"
} as const;

export type InCoreDateTimePickerModes =
    typeof InCoreDateTimePickerModes[keyof typeof InCoreDateTimePickerModes];

export function initializeDateTimePickers(): void {
    $(() => {
        $("input[data-dtp]").each(function () {
            const obj = $(this);
            InCoreDateTimePickers(obj.attr("id")!);
        });
    });
}

export class InCoreDateTimePickerConfig implements flatpickr.Options.Options {
    altInput: boolean;
    noCalendar: boolean;
    enableTime: boolean;
    time_24hr: boolean;
    dateFormat: string;
    altFormat: string;
    minDate?: Date | string | number | undefined;
    maxDate?: Date | string | number | undefined;
    minTime?: Date | string | number | undefined;
    maxTime?: Date | string | number | undefined;
    firstDayOfWeek: number;
    mode: InCoreDateTimePickerModes;
    conjunction: string;
    defaultDate: Date | string | number | undefined;
    inline: boolean;
    locale: key | Partial<CustomLocale>;
    clickOpens: boolean;

    constructor(
        altinput = true,
        nocalendar = false,
        enabletime = false,
        time_24hr = true,
        dateformat = "Y-m-d",
        altformat = "j F Y",
        mindate: Date | string | number | undefined,
        maxdate: Date | string | number | undefined,
        mintime: Date | string | number | undefined,
        maxtime: Date | string | number | undefined,
        firstdayofweek = 1,
        mode = "single" as InCoreDateTimePickerModes,
        conjunction = " => ",
        defaultdate: Date | string | number | undefined,
        inline = false,
        locale = "it" as key,
        clickOpens = true
    ) {
        this.altInput = altinput;
        this.noCalendar = nocalendar;
        this.enableTime = enabletime;
        this.time_24hr = time_24hr;
        this.dateFormat = dateformat;
        this.altFormat = altformat;
        this.minDate = mindate;
        this.maxDate = maxdate;
        this.minTime = mintime;
        this.maxTime = maxtime;
        this.firstDayOfWeek = firstdayofweek;
        this.mode = mode;
        this.conjunction = conjunction;
        this.defaultDate = defaultdate;
        this.inline = inline;
        switch (locale) {
            case "it":
                this.locale = Italian;
                break;
            case "es":
                this.locale = Spanish;
                break;
            case "fr":
                this.locale = French;
                break;
            default:
                this.locale = english;
                break;
        }
        this.clickOpens = clickOpens;
    }
}

/**
 * Initializes a flatpickr date/time picker for the specified input element.
 * @param id - The ID of the input element.
 * @returns The initialized flatpickr instance, or undefined if initialization failed.
 */
export function InCoreDateTimePickers(id: string): flatpickr.Instance | undefined {
    const obj = $(`#${id}`);
    const altInput = (obj.attr("data-altinput") ?? "true") === "true";
    const noCalendar = (obj.attr("data-nocalendar") ?? "false") === "true";
    const enableTime = (obj.attr("data-enabletime") ?? "false") === "true";
    const time_24hr = (obj.attr("data-time24hr") ?? "true") === "true";
    var dateFormat = obj.attr("data-dateformat") ?? "Y-m-d";
    if (enableTime) {
        dateFormat = obj.attr("data-dateformat") ?? "Y-m-d H:i";
    }
    var altFormat = obj.attr("data-altformat") ?? "j F Y";
    if (enableTime) {
        altFormat = obj.attr("data-altformat") ?? "j F Y H:i";
    }
    var raw = obj.attr("data-defaultdate");
    const defaultDate = raw && raw.trim() !== "" ? raw ? new Date(raw) : undefined : undefined;
    raw = obj.attr("data-mindate");
    const minDate = raw && raw.trim() !== "" ? raw ? new Date(raw) : undefined : undefined;
    raw = obj.attr("data-maxdate");
    const maxDate = raw && raw.trim() !== "" ? raw ? new Date(raw) : undefined : undefined;
    raw = obj.attr("data-mintime");
    const minTime = raw && raw.trim() !== "" ? raw ? new Date(raw) : undefined : undefined;
    raw = obj.attr("data-maxtime");
    const maxTime = raw && raw.trim() !== "" ? raw ? new Date(raw) : undefined : undefined;
    const firstDayOfWeek = parseInt(obj.attr("data-firstdayofweek") ?? "1");
    const mode = (obj.attr("data-mode") ?? "single") as InCoreDateTimePickerModes; // single, multiple, range
    const conjunction = obj.attr("data-conjunction") ?? " => ";
    const inline = (obj.attr("data-inline") ?? "false") === "true";
    const locale = obj.attr("data-locale") ?? "it";
    const isReadonly = obj.prop("readonly");
    const isDisabled = obj.prop("disabled");
    const clickOpens = isReadonly || isDisabled;


    return createDateTimePicker(id, altInput, noCalendar, enableTime, time_24hr, dateFormat, altFormat, firstDayOfWeek, mode, conjunction, inline, locale, !clickOpens, defaultDate, minDate, maxDate, minTime, maxTime);
}

export function createDateTimePicker(
    id: string,
    altInput: boolean,
    noCalendar: boolean,
    enableTime: boolean,
    time_24hr: boolean,
    dateFormat: string,
    altFormat: string,
    firstDayOfWeek: number,
    mode: InCoreDateTimePickerModes,
    conjunction: string,
    inline: boolean,
    locale: string,
    clickOpens: boolean,
    defaultDate?: Date | string | number | undefined,
    minDate?: Date | string | number | undefined,
    maxDate?: Date | string | number | undefined,
    minTime?: Date | string | number | undefined,
    maxTime?: Date | string | number | undefined,
): flatpickr.Instance | undefined {

    var config = new InCoreDateTimePickerConfig(altInput, noCalendar, enableTime, time_24hr, dateFormat, altFormat, minDate, maxDate, minTime, maxTime, firstDayOfWeek, mode, conjunction, defaultDate, inline, locale as key, clickOpens);

    var datetimepicker = flatpickr($(`#${id}`)[0], config);

    return datetimepicker;
}
